package com.macrofocus.trenddisplay.timeseriesplot

import com.macrofocus.common.interval.MutableBoundedInterval
import com.macrofocus.common.properties.MutableProperties
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.license.LicenseModel
import com.macrofocus.plot.guide.Guide
import org.mkui.canvas.*
import org.mkui.component.CPComponent
import org.mkui.geom.Rectangle2D
import org.mkui.graphics.colortheme.ColorTheme
import org.mkui.rubberband.RubberBand
import org.mkui.transform.MutableTwoDScreenTransform
import org.molap.aggregates.cube.Group
import org.molap.series.TimeSeries

interface TimeSeriesPlotView<Row, Column> {
    val component : CPComponent

    enum class PropertyType {
        ShowFiltered, ShowGrid, Antialiasing, Rendering, LineWidth, GridThickness, ShowColor, Regression, MarkerSize, ColorTheme, DisplayLabels, Style, Mode, Layout
    }

    enum class Style {
        Step, Straight, Curved
    }

    enum class Layout {
        Overlay, Stack
    }

    val xGuide: Guide?

    val yGuide: Guide?

    fun getClosestGroup(x: Int, y: Int): Group?

    fun getClosestRow(x: Int, y: Int): Row?

    /**
     * Returns the nodes overlapping the specified rectangle.
     *
     * @param rect the rectangle in screen coordinates
     *
     * @return the list of nodes
     */
    fun getRows(rect: Rectangle2D): List<Row>

    val rubberBand: RubberBand?

    var isSelectionMode: Boolean

    /**
     * Returns whether information about refresh rate should be displayed.
     *
     * @return true to display refresh rate information, false otherwise
     */
    /**
     * Sets whether information about refresh rate should be displayed.
     *
     * @param showTiming true to display refresh rate information, false otherwise
     */
    var isShowTiming: Boolean

    fun setLicenseModel(licenseModel: LicenseModel?)

    /**
     * Returns the ScatterPlotModel that provides the data displayed by this ScatterPlotView.
     *
     * @return the ScatterPlotModel that provides the data displayed by this ScatterPlotView
     *
     * @see .setModel
     */
    /**
     * Sets the data model and registers with it for listener notifications from the new data model.
     *
     * @param model the new data model
     *
     * @see .getModel
     */
    var model: TimeSeriesPlotModel<Row, Column>?

    /**
     * Returns the color theme to use.
     *
     * @return the color theme
     */
    /**
     * Sets the color theme to use.
     *
     * @param colorTheme the color theme to use
     */
    var colorTheme: MutableProperty<ColorTheme>

    val xRangeModel: MutableBoundedInterval?

    val yRangeModel: MutableBoundedInterval?

    val screenTransform: MutableTwoDScreenTransform?

    fun zoom(animate: Boolean, x1: Double, x2: Double, y1: Double, y2: Double)

    var showFiltered: MutableProperty<Boolean>

    val showGrid: MutableProperty<Boolean>

    var antialiasing: MutableProperty<Boolean>

    var rendering: MutableProperty<Rendering>

    val lineWidth: MutableProperty<Double>

    val gridThickness: MutableProperty<Float>

    val showColor: MutableProperty<Any?>?

    val regression: MutableProperty<Boolean>

    val markerSize: MutableProperty<Double>

    val displayLabels: MutableProperty<Boolean>

    val mode: MutableProperty<TimeSeries.TimeMode>

    val layout: MutableProperty<TimeSeriesPlotView.Layout>

    val style: MutableProperty<Style>

    val properties: MutableProperties<*>?

    var isMaintainAspectRatio: Boolean

    fun addMouseListener(l: MouseListener)

    fun addMouseMotionListener(l: MouseMotionListener)

    fun addMouseWheelListener(l: MouseWheelListener)

    fun addKeyListener(listener: KeyListener)
}
